/**
 * @module utils/validators
 * @description Validation utility functions for security, files, and data validation
 */

import { logger } from './logger.js';

/**
 * Allowed domains for the application
 * @constant {string[]}
 */
const ALLOWED_DOMAINS = [
    'insightly-archive.web.app',
    'insightly-archive.firebaseapp.com',
    'insightly-archive-staging.web.app',
    'insightly-archive-staging.firebaseapp.com',
    'dc-photos-integrated-staging.web.app',
    'dc-photos-integrated-staging.firebaseapp.com',
    'dc-photos-integrated.web.app',
    'dc-photos-integrated.firebaseapp.com',
    'localhost',
    'insightly-archive.shuttersup.co.uk',
    'staging.insightly-archive.shuttersup.co.uk',
    'dc-photos.shuttersup.co.uk',
    'staging.dc-photos.shuttersup.co.uk'
];

/**
 * Validate that the application is running on an authorized domain
 * Security check to prevent unauthorized deployments
 *
 * @returns {boolean} True if domain is authorized, false otherwise
 *
 * @example
 * if (!validateOrigin()) {
 *   throw new Error('Unauthorized domain access');
 * }
 */
export function validateOrigin() {
    const currentDomain = window.location.hostname;
    const isAllowed = ALLOWED_DOMAINS.some(domain =>
        currentDomain === domain || currentDomain.includes('localhost')
    );

    if (!isAllowed) {
        logger.error(`❌ Unauthorized domain: ${currentDomain}`);
        // Early validation before UI might be initialized, use native alert
        // eslint-disable-next-line no-alert -- Intentional: security validation before UI initialization
        window.alert('This application is not authorized to run on this domain.');
        return false;
    }

    return true;
}

/**
 * Validate file for upload
 * Checks file size and other constraints
 *
 * @param {File} file - File object to validate
 * @param {number} maxSizeMB - Maximum file size in megabytes (default: 10)
 * @returns {boolean} True if file is valid, false otherwise
 *
 * @example
 * const isValid = validateFile(myFile);
 * const isValid = validateFile(myFile, 25); // 25MB limit
 */
export function validateFile(file, maxSizeMB = 10) {
    if (!file) {
        logger.error('❌ No file provided for validation');
        return false;
    }

    const maxSize = maxSizeMB * 1024 * 1024; // Convert MB to bytes

    if (file.size > maxSize) {
        logger.error(`❌ File "${file.name}" is too large. Maximum size is ${maxSizeMB}MB.`);
        return false;
    }

    return true;
}

/**
 * Validate email address format
 *
 * @param {string} email - Email address to validate
 * @returns {boolean} True if email format is valid
 *
 * @example
 * validateEmail('user@example.com');     // true
 * validateEmail('invalid-email');        // false
 */
export function validateEmail(email) {
    if (!email) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Validate email domain
 * Checks if email belongs to a specific domain
 *
 * @param {string} email - Email address to validate
 * @param {string} domain - Required domain (default: 'shuttersup.co.uk')
 * @returns {boolean} True if email belongs to the specified domain
 *
 * @example
 * validateEmailDomain('user@shuttersup.co.uk');              // true
 * validateEmailDomain('user@other.com');                     // false
 * validateEmailDomain('user@example.com', 'example.com');    // true
 */
export function validateEmailDomain(email, domain = 'shuttersup.co.uk') {
    if (!email || !validateEmail(email)) return false;

    return email.toLowerCase().endsWith(`@${domain.toLowerCase()}`);
}

/**
 * Validate URL format
 *
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL format is valid
 *
 * @example
 * validateUrl('https://example.com');     // true
 * validateUrl('not-a-url');               // false
 */
export function validateUrl(url) {
    if (!url) return false;

    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * Validate entity ID format
 * Checks if ID is a valid integer or string ID
 *
 * @param {string|number} id - Entity ID to validate
 * @returns {boolean} True if ID is valid
 *
 * @example
 * validateEntityId('12345');    // true
 * validateEntityId(12345);      // true
 * validateEntityId('');         // false
 * validateEntityId(null);       // false
 */
export function validateEntityId(id) {
    if (!id && id !== 0) return false;

    // Check if it's a number or can be parsed as a number
    if (typeof id === 'number') return !isNaN(id);
    if (typeof id === 'string') return id.trim().length > 0;

    return false;
}

/**
 * Validate search term length
 * Ensures search term meets minimum requirements
 *
 * @param {string} searchTerm - Search term to validate
 * @param {number} minLength - Minimum required length (default: 3)
 * @returns {boolean} True if search term is valid
 *
 * @example
 * validateSearchTerm('test');      // true
 * validateSearchTerm('ab');        // false (too short)
 * validateSearchTerm('abc', 5);    // false (min 5 chars)
 */
export function validateSearchTerm(searchTerm, minLength = 3) {
    if (!searchTerm || typeof searchTerm !== 'string') return false;

    return searchTerm.trim().length >= minLength;
}

/**
 * Check if value is defined and not null
 *
 * @param {*} value - Value to check
 * @returns {boolean} True if value is defined and not null
 */
export function isDefined(value) {
    return value !== undefined && value !== null;
}

/**
 * Check if string is empty or only whitespace
 *
 * @param {string} str - String to check
 * @returns {boolean} True if string is empty or whitespace
 */
export function isEmpty(str) {
    return !str || (typeof str === 'string' && str.trim().length === 0);
}
